import { useEffect, type FC } from 'react'

interface SEOProps {
  readonly title: string
  readonly description: string
  readonly keywords: string
  readonly canonical?: string
  readonly robots?: string
  readonly image?: string
}

// Capture initial defaults from the document head once
let DEFAULT_TITLE: string | null = null
let DEFAULT_DESCRIPTION: string | null = null
let DEFAULT_KEYWORDS: string | null = null
let DEFAULT_CANONICAL: string | null = null
let DEFAULT_ROBOTS: string | null = null
let DEFAULT_OG_TITLE: string | null = null
let DEFAULT_OG_DESCRIPTION: string | null = null
let DEFAULT_OG_URL: string | null = null
let DEFAULT_TWITTER_TITLE: string | null = null
let DEFAULT_TWITTER_DESCRIPTION: string | null = null
let DEFAULT_TWITTER_CARD: string | null = null
let DEFAULT_TWITTER_IMAGE: string | null = null

const ensureDefaults = () => {
  if (DEFAULT_TITLE !== null) return
  const currentTitle = typeof document.title === 'string' ? document.title : ''
  DEFAULT_TITLE = currentTitle.trim().length > 0 ? currentTitle : null
  const descriptionEl = document.querySelector<HTMLMetaElement>('meta[name="description"]')
  const keywordsEl = document.querySelector<HTMLMetaElement>('meta[name="keywords"]')
  const canonicalEl = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  const robotsEl = document.querySelector<HTMLMetaElement>('meta[name="robots"]')
  const ogTitleEl = document.querySelector<HTMLMetaElement>('meta[property="og:title"]')
  const ogDescEl = document.querySelector<HTMLMetaElement>('meta[property="og:description"]')
  const ogUrlEl = document.querySelector<HTMLMetaElement>('meta[property="og:url"]')
  const twitterTitleEl = document.querySelector<HTMLMetaElement>('meta[name="twitter:title"]')
  const twitterDescEl = document.querySelector<HTMLMetaElement>('meta[name="twitter:description"]')
  const twitterCardEl = document.querySelector<HTMLMetaElement>('meta[name="twitter:card"]')
  const twitterImageEl = document.querySelector<HTMLMetaElement>('meta[name="twitter:image"]')
  DEFAULT_DESCRIPTION = descriptionEl ? descriptionEl.getAttribute('content') : null
  DEFAULT_KEYWORDS = keywordsEl ? keywordsEl.getAttribute('content') : null
  DEFAULT_CANONICAL = canonicalEl ? canonicalEl.getAttribute('href') : null
  DEFAULT_ROBOTS = robotsEl ? robotsEl.getAttribute('content') : null
  DEFAULT_OG_TITLE = ogTitleEl ? ogTitleEl.getAttribute('content') : null
  DEFAULT_OG_DESCRIPTION = ogDescEl ? ogDescEl.getAttribute('content') : null
  DEFAULT_OG_URL = ogUrlEl ? ogUrlEl.getAttribute('content') : null
  DEFAULT_TWITTER_TITLE = twitterTitleEl ? twitterTitleEl.getAttribute('content') : null
  DEFAULT_TWITTER_DESCRIPTION = twitterDescEl ? twitterDescEl.getAttribute('content') : null
  DEFAULT_TWITTER_CARD = twitterCardEl ? twitterCardEl.getAttribute('content') : null
  DEFAULT_TWITTER_IMAGE = twitterImageEl ? twitterImageEl.getAttribute('content') : null
}

export const SEO: FC<SEOProps> = ({ title, description, keywords, canonical, robots, image }) => {
  useEffect(() => {
    ensureDefaults()

    document.title = title

    const createdNodes: HTMLElement[] = []

    const setNamedMeta = (name: string, content: string, createIfMissing = false) => {
      let element = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
      if (!element && createIfMissing) {
        element = document.createElement('meta')
        element.setAttribute('name', name)
        document.head.appendChild(element)
        createdNodes.push(element)
      }
      if (element) {
        element.setAttribute('content', content)
      }
    }

    const setPropertyMeta = (property: string, content: string, createIfMissing = false) => {
      let element = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`)
      if (!element && createIfMissing) {
        element = document.createElement('meta')
        element.setAttribute('property', property)
        document.head.appendChild(element)
        createdNodes.push(element)
      }
      if (element) {
        element.setAttribute('content', content)
      }
    }

    setNamedMeta('description', description)
    setNamedMeta('keywords', keywords)

    if (canonical) {
      let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
      if (!link) {
        link = document.createElement('link')
        link.setAttribute('rel', 'canonical')
        document.head.appendChild(link)
      }
      link.setAttribute('href', canonical)
    }

    // Open Graph
    setPropertyMeta('og:title', title)
    setPropertyMeta('og:description', description)
    if (canonical) {
      setPropertyMeta('og:url', canonical)
    }

    // Twitter
    setNamedMeta('twitter:card', DEFAULT_TWITTER_CARD ?? 'summary', true)
    setNamedMeta('twitter:title', title, true)
    setNamedMeta('twitter:description', description, true)
    if (image) {
      setNamedMeta('twitter:image', image, true)
    }

    // Robots (e.g., for 404 page)
    if (robots) {
      setNamedMeta('robots', robots, true)
    }

    // On unmount (or before next update), restore defaults
    return () => {
      document.title = DEFAULT_TITLE ?? ''
      const descriptionEl = document.querySelector<HTMLMetaElement>('meta[name="description"]')
      if (descriptionEl && DEFAULT_DESCRIPTION !== null) {
        descriptionEl.setAttribute('content', DEFAULT_DESCRIPTION)
      }
      const keywordsEl = document.querySelector<HTMLMetaElement>('meta[name="keywords"]')
      if (keywordsEl && DEFAULT_KEYWORDS !== null) {
        keywordsEl.setAttribute('content', DEFAULT_KEYWORDS)
      }
      const canonicalEl = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
      if (canonicalEl && DEFAULT_CANONICAL !== null) {
        canonicalEl.setAttribute('href', DEFAULT_CANONICAL)
      }
      // Restore OG
      const ogTitleEl = document.querySelector<HTMLMetaElement>('meta[property="og:title"]')
      if (ogTitleEl && DEFAULT_OG_TITLE !== null) {
        ogTitleEl.setAttribute('content', DEFAULT_OG_TITLE)
      }
      const ogDescEl = document.querySelector<HTMLMetaElement>('meta[property="og:description"]')
      if (ogDescEl && DEFAULT_OG_DESCRIPTION !== null) {
        ogDescEl.setAttribute('content', DEFAULT_OG_DESCRIPTION)
      }
      const ogUrlEl = document.querySelector<HTMLMetaElement>('meta[property="og:url"]')
      if (ogUrlEl && DEFAULT_OG_URL !== null) {
        ogUrlEl.setAttribute('content', DEFAULT_OG_URL)
      }
      // Restore Twitter
      const twTitleEl = document.querySelector<HTMLMetaElement>('meta[name="twitter:title"]')
      if (twTitleEl && DEFAULT_TWITTER_TITLE !== null) {
        twTitleEl.setAttribute('content', DEFAULT_TWITTER_TITLE)
      }
      const twDescEl = document.querySelector<HTMLMetaElement>('meta[name="twitter:description"]')
      if (twDescEl && DEFAULT_TWITTER_DESCRIPTION !== null) {
        twDescEl.setAttribute('content', DEFAULT_TWITTER_DESCRIPTION)
      }
      const twCardEl = document.querySelector<HTMLMetaElement>('meta[name="twitter:card"]')
      if (twCardEl && DEFAULT_TWITTER_CARD !== null) {
        twCardEl.setAttribute('content', DEFAULT_TWITTER_CARD)
      }
      const twImgEl = document.querySelector<HTMLMetaElement>('meta[name="twitter:image"]')
      if (twImgEl && DEFAULT_TWITTER_IMAGE !== null) {
        twImgEl.setAttribute('content', DEFAULT_TWITTER_IMAGE)
      }
      // Restore robots
      const robotsEl = document.querySelector<HTMLMetaElement>('meta[name="robots"]')
      if (robotsEl) {
        if (DEFAULT_ROBOTS !== null) {
          robotsEl.setAttribute('content', DEFAULT_ROBOTS)
        } else {
          // If it didn't exist originally, remove it (created by this component)
          document.head.removeChild(robotsEl)
        }
      }
      // Remove any meta tags we created during this mount (e.g., twitter when not present by default)
      createdNodes.forEach((node) => {
        if (node.parentNode) {
          node.parentNode.removeChild(node)
        }
      })
    }
  }, [title, description, keywords, canonical, robots, image])

  return null
}
